
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    InputAdornment,
    CircularProgress,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
    Fade,
    Collapse,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faLock, faMobileAlt, faCreditCard, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { PayPalButtons, FUNDING, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import assets
import MtnLogo from '../../../../assets/Ourpartners/mtn.png';
import AirtelLogo from '../../../../assets/Ourpartners/airtel.png';
import baseURL from '../../user/config';
import COLORS from '../../../../constants/colors';

// --- UTILITIES ---
let cachedRate = null;
let rateFetchedAt = 0;
const RATE_CACHE_MS = 10 * 60 * 1000;

const getExchangeRate = async () => {
    if (cachedRate && Date.now() - rateFetchedAt < RATE_CACHE_MS) {
        return cachedRate;
    }
    try {
        const res = await fetch(`${baseURL}/paypal/exchange-rate`);
        const data = await res.json();
        cachedRate = data;
        rateFetchedAt = Date.now();
        return data;
    } catch {
        return { UGX_TO_USD: 3300, USD_TO_UGX: 3300, UGX_TO_USD_RATE: 1 / 3300 };
    }
};

const convertCurrency = async (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;

    const rates = await getExchangeRate();

    if (fromCurrency === 'UGX' && toCurrency === 'USD') {
        return (amount * rates.UGX_TO_USD_RATE).toFixed(2);
    }

    if (fromCurrency === 'USD' && toCurrency === 'UGX') {
        return Math.round(amount * rates.USD_TO_UGX);
    }
    return amount;
};

const toSentenceCase = (str) => {
    return str
        .toLowerCase()
        .split(' ')
        .filter(word => word.trim() !== '')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
};

// --- STYLES ---
const styles = {
    container: {
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f7fa 100%)',
        minHeight: '100vh',
        p: { xs: 1.5, sm: 2 },
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    card: {
        maxWidth: 480,
        mx: 'auto',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
    },
    header: {
        position: 'relative',
        height: 120,
        overflow: 'hidden',
    },
    headerImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    headerOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)',
        p: 2,
        pt: 4,
    },
    content: {
        p: { xs: 2, sm: 3 },
    },
    progressSection: {
        mb: 3,
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
        bgcolor: 'rgba(0,0,0,0.06)',
        '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
        },
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            },
            '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${COLORS.primary}20`,
            },
        },
    },
    toggleButtonGroup: {
        width: '100%',
        '& .MuiToggleButton-root': {
            flex: 1,
            py: 1.5,
            borderRadius: '12px !important',
            border: '1px solid #e0e0e0 !important',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.85rem',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
                bgcolor: `${COLORS.primary}10`,
                borderColor: `${COLORS.primary} !important`,
                color: COLORS.primary,
            },
            '&:hover': {
                bgcolor: 'rgba(0,0,0,0.02)',
            },
        },
    },
    providerButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 2,
        borderRadius: 2,
        border: '2px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        bgcolor: '#fff',
        '&:hover': {
            borderColor: '#e0e0e0',
            transform: 'translateY(-2px)',
        },
    },
    providerButtonSelected: {
        borderColor: COLORS.primary,
        bgcolor: `${COLORS.primary}08`,
    },
    donateButton: {
        py: 1.5,
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '1rem',
        bgcolor: COLORS.primary,
        color: '#fff',
        boxShadow: `0 4px 16px ${COLORS.primary}40`,
        transition: 'all 0.3s ease',
        '&:hover': {
            bgcolor: COLORS.secondary,
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 24px ${COLORS.primary}50`,
        },
        '&:active': {
            transform: 'translateY(0)',
        },
    },
    footer: {
        mt: 2.5,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        opacity: 0.6,
    },
};

const EmbedDonatePage = () => {
    const [searchParams] = useSearchParams();
    const campaignId = searchParams.get('campaignId');

    // Campaign State
    const [campaign, setCampaign] = useState(null);
    const [loadingCampaign, setLoadingCampaign] = useState(true);
    const [error, setError] = useState(null);

    // Form State
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('UGX');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('mobile');
    const [paymentProvider, setPaymentProvider] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [processing, setProcessing] = useState(false);

    // PayPal State
    const [paypalClientId, setPaypalClientId] = useState(null);
    const [paypalLoading, setPaypalLoading] = useState(true);

    // Preset amounts
    const presetAmounts = currency === 'UGX'
        ? [10000, 25000, 50000, 100000]
        : [5, 10, 25, 50];

    useEffect(() => {
        const fetchCampaign = async () => {
            if (!campaignId) {
                setError("No campaign specified");
                setLoadingCampaign(false);
                return;
            }

            try {
                const res = await fetch(`${baseURL}/embed/campaign/${campaignId}`);
                if (!res.ok) throw new Error("Campaign not found or closed");
                const data = await res.json();
                setCampaign(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingCampaign(false);
            }
        };

        const fetchPaypalConfig = async () => {
            try {
                const res = await fetch(`${baseURL}/paypal/config`);
                const data = await res.json();
                if (res.ok && data.paypalClientId) {
                    setPaypalClientId(data.paypalClientId);
                }
            } catch (err) {
                console.error("PayPal config error", err);
            } finally {
                setPaypalLoading(false);
            }
        };

        fetchCampaign();
        fetchPaypalConfig();
    }, [campaignId]);

    // Send height update to parent
    useEffect(() => {
        const sendHeight = () => {
            const height = document.body.scrollHeight;
            window.parent.postMessage({
                type: 'yamba-resize',
                height: height,
                campaignId: campaignId
            }, '*');
        };

        sendHeight();
        window.addEventListener('resize', sendHeight);
        const observer = new ResizeObserver(sendHeight);
        observer.observe(document.body);

        return () => {
            window.removeEventListener('resize', sendHeight);
            observer.disconnect();
        };
    }, [campaign, paymentMethod, error, processing, campaignId]);

    const handleDonate = async () => {
        if (!name || !amount) {
            toast.error("Please fill in required fields");
            return;
        }

        setProcessing(true);
        let finalAmount = parseFloat(amount);
        let finalCurrency = currency;

        try {
            if (paymentMethod === 'mobile') {
                if (!paymentProvider) {
                    toast.error("Please select a mobile money provider");
                    setProcessing(false);
                    return;
                }
                if (!phoneNumber) {
                    toast.error("Please enter your phone number");
                    setProcessing(false);
                    return;
                }

                if (currency === 'USD') {
                    finalAmount = await convertCurrency(finalAmount, 'USD', 'UGX');
                    finalCurrency = 'UGX';
                }

                const donationData = {
                    donationAmount: finalAmount,
                    currency: finalCurrency,
                    paymentMethod: 'Mobile Money',
                    phoneNumber,
                    paymentProvider,
                    email,
                    name: toSentenceCase(name),
                    showEmail: true,
                    campaignId: campaign.id,
                    campaignName: campaign.name
                };

                const res = await fetch(`${baseURL}/data/backend/donate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(donationData)
                });
                const data = await res.json();

                if (res.ok) {
                    toast.success("Donation initiated! Check your phone.");
                    window.parent.postMessage({ type: 'yamba-success', method: 'Mobile Money' }, '*');
                } else {
                    toast.error(data.message || "Failed to initiate payment");
                }
            } else if (paymentMethod === 'card') {
                if (!email) {
                    toast.error("Email is required for card payment");
                    setProcessing(false);
                    return;
                }

                if (currency === 'UGX') {
                    finalAmount = await convertCurrency(finalAmount, 'UGX', 'USD');
                    finalCurrency = 'USD';
                }

                const res = await fetch(`${baseURL}/data/backend/paystack/initiate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        campaignId: campaign.id,
                        campaignName: campaign.name,
                        name: toSentenceCase(name),
                        email,
                        amount: finalAmount
                    })
                });
                const data = await res.json();

                if (res.ok && data.authorization_url) {
                    window.top.location.href = data.authorization_url;
                } else {
                    toast.error("Payment initiation failed");
                }
            }
        } catch (err) {
            console.error(err);
            toast.error("An error occurred");
        } finally {
            setProcessing(false);
        }
    };

    // PayPal Handlers
    const createPayPalOrder = async (data, actions) => {
        let finalAmount = parseFloat(amount);
        if (currency === 'UGX') {
            finalAmount = await convertCurrency(finalAmount, 'UGX', 'USD');
        }

        try {
            const res = await fetch(`${baseURL}/paypal/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: finalAmount,
                    currency: 'USD',
                    name: toSentenceCase(name),
                    email,
                    campaignId: campaign.id,
                    campaignName: campaign.name
                })
            });
            const order = await res.json();
            if (!res.ok) throw new Error(order.message);
            return order.orderID;
        } catch (err) {
            toast.error("PayPal Error: " + err.message);
            return null;
        }
    };

    const onPayPalApprove = async (data, actions) => {
        try {
            const res = await fetch(`${baseURL}/paypal/capture-order/${data.orderID}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const captureData = await res.json();

            if (captureData.status === 'COMPLETED') {
                toast.success(captureData.message || "Donation Successful!");
                window.parent.postMessage({ type: 'yamba-success', method: 'PayPal' }, '*');
            } else if (captureData.status === 'PROCESSING') {
                toast.info(captureData.message || "Payment is being processed. You'll receive a confirmation email.");
                window.parent.postMessage({ type: 'yamba-success', method: 'PayPal' }, '*');
            } else {
                toast.error(captureData.message || "Payment capture failed. Please try again.");
            }
        } catch (err) {
            console.error("PayPal capture error:", err);
            toast.error("An error occurred during payment capture.");
        }
    };

    // --- RENDER ---
    if (loadingCampaign) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress sx={{ color: COLORS.primary }} />
        </Box>
    );

    if (error) return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>Something went wrong</Typography>
            <Typography color="text.secondary">{error}</Typography>
        </Box>
    );

    return (
        <Box sx={styles.container}>
            <ToastContainer position="top-center" autoClose={3000} hideProgressBar />

            <Fade in timeout={400}>
                <Box sx={styles.card}>
                    {/* Header */}
                    <Box sx={styles.header}>
                        {campaign.image_url ? (
                            <Box component="img" src={campaign.image_url} alt={campaign.name} sx={styles.headerImage} />
                        ) : (
                            <Box sx={{ height: '100%', bgcolor: COLORS.primary, opacity: 0.1 }} />
                        )}
                        <Box sx={styles.headerOverlay}>
                            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
                                {campaign.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                by {campaign.owner_name}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={styles.content}>
                        {/* Progress */}
                        <Box sx={styles.progressSection}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" fontWeight={600} color={COLORS.primary}>
                                    {campaign.progress}% funded
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Goal: {formatNumber(campaign.goal)} UGX
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(parseFloat(campaign.progress), 100)}
                                sx={styles.progressBar}
                            />
                        </Box>

                        {/* Donation Form */}
                        <Box component="form" noValidate>
                            {/* Name Field */}
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Your name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                sx={{ ...styles.textField, mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FontAwesomeIcon icon={faUser} color={COLORS.secondary} size="sm" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Amount Section */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    <Select
                                        value={currency}
                                        onChange={(e) => {
                                            setCurrency(e.target.value);
                                            setAmount('');
                                        }}
                                        size="small"
                                        sx={{
                                            width: 90,
                                            borderRadius: 2,
                                            bgcolor: '#fff',
                                        }}
                                    >
                                        <MenuItem value="UGX">UGX</MenuItem>
                                        <MenuItem value="USD">USD</MenuItem>
                                    </Select>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Amount"
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        sx={styles.textField}
                                    />
                                </Box>

                                {/* Preset Amounts */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {presetAmounts.map((preset) => (
                                        <Button
                                            key={preset}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setAmount(preset.toString())}
                                            sx={{
                                                flex: 1,
                                                minWidth: 'auto',
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                borderColor: amount === preset.toString() ? COLORS.primary : '#e0e0e0',
                                                color: amount === preset.toString() ? COLORS.primary : 'text.secondary',
                                                bgcolor: amount === preset.toString() ? `${COLORS.primary}08` : 'transparent',
                                            }}
                                        >
                                            {formatNumber(preset)}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>

                            {/* Email Field */}
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Email (for receipt)"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={{ ...styles.textField, mb: 2.5 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FontAwesomeIcon icon={faEnvelope} color={COLORS.secondary} size="sm" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Payment Method Toggle */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Payment Method
                                </Typography>
                                <ToggleButtonGroup
                                    value={paymentMethod}
                                    exclusive
                                    onChange={(e, val) => val && setPaymentMethod(val)}
                                    sx={styles.toggleButtonGroup}
                                >
                                    <ToggleButton value="mobile">
                                        <FontAwesomeIcon icon={faMobileAlt} style={{ marginRight: 8 }} />
                                        Mobile Money
                                    </ToggleButton>
                                    <ToggleButton value="card">
                                        <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 8 }} />
                                        Card
                                    </ToggleButton>
                                    <ToggleButton value="paypal">
                                        <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 8 }} />
                                        PayPal
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Mobile Money Provider Selection */}
                            <Collapse in={paymentMethod === 'mobile'}>
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                        Select Provider
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box
                                            onClick={() => setPaymentProvider('MTN')}
                                            sx={{
                                                ...styles.providerButton,
                                                ...(paymentProvider === 'MTN' && styles.providerButtonSelected),
                                            }}
                                        >
                                            <Box component="img" src={MtnLogo} alt="MTN" sx={{ height: 32 }} />
                                            <Typography variant="caption" fontWeight={500}>MTN MoMo</Typography>
                                        </Box>
                                        <Box
                                            onClick={() => setPaymentProvider('Airtel')}
                                            sx={{
                                                ...styles.providerButton,
                                                ...(paymentProvider === 'Airtel' && styles.providerButtonSelected),
                                            }}
                                        >
                                            <Box component="img" src={AirtelLogo} alt="Airtel" sx={{ height: 32 }} />
                                            <Typography variant="caption" fontWeight={500}>Airtel Money</Typography>
                                        </Box>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Phone number (07XXXXXXXX)"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        sx={{ ...styles.textField, mt: 2 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FontAwesomeIcon icon={faPhone} color={COLORS.secondary} size="sm" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Collapse>

                            {/* Action Button */}
                            {paymentMethod === 'paypal' ? (
                                paypalLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : paypalClientId ? (
                                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD", intent: "capture" }}>
                                        <PayPalButtons
                                            fundingSource={FUNDING.PAYPAL}
                                            createOrder={createPayPalOrder}
                                            onApprove={onPayPalApprove}
                                            style={{ layout: "vertical", height: 45, borderRadius: 8 }}
                                        />
                                    </PayPalScriptProvider>
                                ) : (
                                    <Typography variant="body2" color="error" textAlign="center">
                                        PayPal is currently unavailable
                                    </Typography>
                                )
                            ) : (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleDonate}
                                    disabled={processing || !name || !amount}
                                    sx={styles.donateButton}
                                >
                                    {processing ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        `Donate ${currency} ${amount ? formatNumber(parseFloat(amount)) : ''}`
                                    )}
                                </Button>
                            )}

                            {/* Footer */}
                            <Box sx={styles.footer}>
                                <FontAwesomeIcon icon={faLock} size="xs" />
                                <Typography variant="caption">
                                    Secure donation powered by Yamba Africa
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Box>
    );
};

export default EmbedDonatePage;
